import React, { useState, useEffect } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import './Reviews.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Google Business Profile URL for reviews
const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJ7dUw4PzVukcRp9SMSlueft0";
const GOOGLE_ALL_REVIEWS_URL = "https://www.google.com/maps/place/Nessenius+Gartenservice/@51.5729,9.9372,17z/data=!4m8!3m7!1s0x47b9d5fcf030d5ed:0xdd7e5b4b8a4c6ca7!8m2!3d51.5729!4d9.9372!9m1!1b1!16s";

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [reviewsData, setReviewsData] = useState({ average_rating: 5.0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Kundenbewertungen | Nessenius Garten & Landschaftsbau';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Kundenbewertungen für Nessenius Garten & Landschaftsbau. 4.9 von 5 Sternen auf Google. Lesen Sie, was unsere Kunden in Göttingen und Umgebung sagen.');
    
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          setReviews(data.reviews || []);
          setReviewsData({
            average_rating: data.average_rating || 5.0,
            total_reviews: data.total_reviews || data.reviews?.length || 0
          });
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  return (
    <div className="reviews-page">
      <section className="reviews-hero">
        <div className="container">
          <h1>Kundenbewertungen</h1>
          <div className="reviews-header-rating">
            <div className="stars-large">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={32} 
                  fill={i < Math.floor(reviewsData.average_rating) ? "#FFB800" : "none"} 
                  color="#FFB800"
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <p className="rating-text">
              {reviewsData.average_rating.toFixed(1)} von 5 Sternen • {reviewsData.total_reviews} Bewertungen auf Google
            </p>
          </div>
        </div>
      </section>

      <section className="reviews-content-section">
        <div className="container">
          <div className="google-reviews-notice network-card">
            <h3 className="heading-3">Waren Sie zufrieden? Bewerten Sie uns!</h3>
            <p className="body-medium" style={{ marginTop: '12px' }}>
              Ihre Meinung ist uns wichtig. Wenn Sie mit unserem Service zufrieden waren, freuen wir uns über Ihre Bewertung auf Google!
            </p>
            <div className="reviews-notice-buttons">
              <a 
                href={GOOGLE_REVIEW_URL}
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="write-review-btn"
              >
                <ExternalLink size={18} style={{ marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }} />
                Jetzt bewerten
              </a>
              <a 
                href={GOOGLE_ALL_REVIEWS_URL}
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary-outline"
                data-testid="all-reviews-google-btn"
              >
                Alle {reviewsData.total_reviews} Bewertungen auf Google ansehen
              </a>
            </div>
          </div>

          {loading ? (
            <div className="reviews-loading">
              <p>Bewertungen werden geladen...</p>
            </div>
          ) : (
            <>
              <div className="reviews-section-header">
                <h3>Kundenbewertungen von Google ({reviews.length})</h3>
              </div>
              
              <div className="reviews-full-grid">
                {reviews.map((review, index) => (
                  <div key={review.id || index} className="review-full-card network-card" data-testid={`review-card-${index}`}>
                    <div className="review-header">
                      <div className="review-author-info">
                        <div className="review-author-avatar">
                          {review.author_photo ? (
                            <img src={review.author_photo} alt={review.author} />
                          ) : (
                            review.author?.charAt(0) || 'A'
                          )}
                        </div>
                        <div>
                          <div className="review-author-name body-medium" style={{ fontWeight: 600 }}>
                            {review.author}
                          </div>
                          <div className="review-date caption">
                            {review.relative_time || 'Google Bewertung'}
                          </div>
                        </div>
                      </div>
                      <div className="review-stars-full">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            size={18} 
                            fill={i < review.rating ? "var(--brand-primary)" : "#e0e0e0"} 
                            color={i < review.rating ? "var(--brand-primary)" : "#e0e0e0"} 
                          />
                        ))}
                      </div>
                    </div>
                    <p className="body-medium review-text">"{review.text}"</p>
                  </div>
                ))}
              </div>

              {/* Hinweis für alle Bewertungen */}
              {reviewsData.total_reviews > reviews.length && (
                <div className="all-reviews-hint">
                  <p>
                    Hier werden {reviews.length} Bewertungen angezeigt. 
                    Alle {reviewsData.total_reviews} Bewertungen finden Sie direkt auf Google.
                  </p>
                  <a 
                    href={GOOGLE_ALL_REVIEWS_URL}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-cta"
                    data-testid="all-reviews-google-btn-bottom"
                  >
                    <Star size={20} style={{ marginRight: '8px' }} />
                    Alle {reviewsData.total_reviews} Bewertungen auf Google lesen
                  </a>
                </div>
              )}
            </>
          )}

          {/* Bottom CTA */}
          <div className="reviews-bottom-cta">
            <h3>Zufrieden mit unserem Service?</h3>
            <p>Teilen Sie Ihre Erfahrung mit anderen!</p>
            <a 
              href={GOOGLE_REVIEW_URL}
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="write-review-btn-bottom"
            >
              <Star size={20} style={{ marginRight: '8px' }} />
              Jetzt auf Google bewerten
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
